import { useCallback, useEffect, useRef } from 'react'
import * as Sentry from '@sentry/react'
import type { Node, Edge, ReactFlowInstance } from '@xyflow/react'
import type { MindMapNode, MindMapEdge, StakeholderRatings, IdeationPhase } from '@bandos/shared'
import type { ReactFlowNodeData, IdeaNodeData } from '../../../types/mindmap-components'
import type { SaveQueueContextValue } from '../../../context/SaveQueueContextTypes'
import { toBackendNodeFormat } from '../../../utils'
import { validateMindmapData } from '../../../utils/duplicateDetection'
import {
  transformAIDataToReactFlow,
  mergeAINodesWithExisting,
  mergeAIEdgesWithExisting,
} from '../utils'
import { AIService } from '../../../services/ai/aiService'
import { useAI } from './useAI'
import type { UseAIReturn } from '../types/aiTypes'
import { createAIStateStore, type AIStateStore } from './AIStateStore'

type SetNodes = React.Dispatch<React.SetStateAction<Node<ReactFlowNodeData>[]>>
type SetEdges = React.Dispatch<React.SetStateAction<Edge[]>>

interface UseAIOperationsDeps {
  mindmapId: string | undefined
  nodes: Node<ReactFlowNodeData>[]
  edges: Edge[]
  setNodes: SetNodes
  setEdges: SetEdges
  reactFlowInstanceRef: React.RefObject<ReactFlowInstance<Node<ReactFlowNodeData>, Edge> | null>
  saveQueue: SaveQueueContextValue
}

export interface UseAIOperationsReturn {
  ai: UseAIReturn
  aiStateStoreRef: React.RefObject<AIStateStore>
  addAIGeneratedNodes: (nodes: MindMapNode[], edges: MindMapEdge[]) => void
  generateFeedbackForIdea: (nodeId: string, onlyForStakeholderNames?: string[]) => Promise<void>
  regenerateFeedbackForChildren: (sourceNodeId: string) => void
  handleGenerateIdeas: (nodeId: string) => Promise<void>
  handleGenerateNeedsFromVenture: (nodeId: string) => Promise<void>
  handleSynthesizeNeedsFromResearch: (nodeId: string) => Promise<void>
}

export function useAIOperations({
  mindmapId,
  nodes,
  edges,
  setNodes,
  setEdges,
  reactFlowInstanceRef,
  saveQueue,
}: UseAIOperationsDeps): UseAIOperationsReturn {
  const aiStateStoreRef = useRef<AIStateStore>(createAIStateStore())

  const getCurrentNodes = useCallback((): Node<ReactFlowNodeData>[] => {
    return reactFlowInstanceRef.current?.getNodes() ?? []
  }, [reactFlowInstanceRef])

  /**
   * Adds AI-generated nodes and edges to the mindmap.
   * Transforms backend format to React Flow format.
   */
  const addAIGeneratedNodes = useCallback(
    (aiNodes: MindMapNode[], aiEdges: MindMapEdge[]) => {
      validateMindmapData(aiNodes, aiEdges)
      const { nodes: transformedNodes, edges: transformedEdges } = transformAIDataToReactFlow(
        aiNodes,
        aiEdges,
        { mindmapId }
      )
      setNodes((existingNodes) => mergeAINodesWithExisting(existingNodes, transformedNodes))
      setEdges((existingEdges) => mergeAIEdgesWithExisting(existingEdges, transformedEdges))
    },
    [mindmapId, setNodes, setEdges]
  )

  /**
   * Generates feedback/ratings for an idea node.
   */
  const generateFeedbackForIdea = useCallback(
    async (nodeId: string, onlyForStakeholderNames: string[] = []) => {
      if (!mindmapId) return

      if (onlyForStakeholderNames.length === 0) return

      const generatingNames = onlyForStakeholderNames

      setNodes((currentNodes) =>
        currentNodes.map((node) => {
          if (node.id === nodeId && node.data.category === 'idea') {
            const ideaData = node.data as IdeaNodeData
            const existing = ideaData.generatingRatingNames ?? []
            return {
              ...node,
              data: {
                ...node.data,
                generatingRatingNames: [...existing, ...generatingNames],
              },
            }
          }
          return node
        })
      )

      try {
        await saveQueue.waitForPendingSaves()
        const response = await AIService.generateRatings(mindmapId, nodeId, onlyForStakeholderNames)

        if (response.error) throw new Error(response.error.message)

        const ratings = response.data?.ratings
        const hasActualRatings = ratings != null && ratings.stakeholders.length > 0

        setNodes((currentNodes) =>
          currentNodes.map((node) => {
            if (node.id === nodeId && node.data.category === 'idea') {
              const ideaData = node.data as IdeaNodeData
              const existingRatings = ideaData.stakeholderRating

              let newRatings: StakeholderRatings | undefined
              if (existingRatings && hasActualRatings) {
                newRatings = {
                  stakeholders: [...existingRatings.stakeholders, ...ratings.stakeholders],
                }
              } else if (hasActualRatings) {
                newRatings = ratings as StakeholderRatings
              } else {
                newRatings = existingRatings
              }

              const updatedPhase =
                (response.data?.phase as IdeationPhase | undefined) ?? node.data.phase

              const remainingNames = (ideaData.generatingRatingNames ?? []).filter(
                (n: string) => !generatingNames.includes(n)
              )
              return {
                ...node,
                data: {
                  ...node.data,
                  stakeholderRating: newRatings,
                  generatingRatingNames: remainingNames,
                  phase: updatedPhase,
                },
              }
            }
            return node
          })
        )
      } catch (error) {
        Sentry.captureException(error, {
          tags: { feature: 'mindmap', action: 'generate_feedback_for_idea' },
        })
        setNodes((currentNodes) =>
          currentNodes.map((node) => {
            if (node.id === nodeId && node.data.category === 'idea') {
              const ideaData = node.data as IdeaNodeData
              const remainingNames = (ideaData.generatingRatingNames ?? []).filter(
                (n: string) => !generatingNames.includes(n)
              )
              return { ...node, data: { ...node.data, generatingRatingNames: remainingNames } }
            }
            return node
          })
        )
      }
    },
    [mindmapId, setNodes, saveQueue]
  )

  /**
   * Regenerates feedback for all existing idea children of a source node.
   */
  const regenerateFeedbackForChildren = useCallback(
    (sourceNodeId: string) => {
      const currentEdges = reactFlowInstanceRef.current?.getEdges() ?? []
      const currentNodes = reactFlowInstanceRef.current?.getNodes() ?? []

      const ideaChildIds = currentEdges
        .filter((edge) => edge.source === sourceNodeId)
        .map((edge) => edge.target)
        .filter((targetId) => {
          const node = currentNodes.find((n) => n.id === targetId)
          return node?.data.category === 'idea'
        })

      ideaChildIds.forEach((ideaId) => {
        void generateFeedbackForIdea(ideaId)
      })
    },
    [generateFeedbackForIdea, reactFlowInstanceRef]
  )

  // AI hook - manages AI operations, suggestions, and AI-related state
  const ai = useAI({
    mindmapId,
    onAddNodes: addAIGeneratedNodes,
    onRemoveNodes: (nodeIds: string[]) => {
      setNodes((prev) => prev.filter((node) => !nodeIds.includes(node.id)))
      setEdges((prev) =>
        prev.filter((edge) => !nodeIds.includes(edge.source) && !nodeIds.includes(edge.target))
      )
    },
    onSuccess: (operation, result) => {
      if (operation === 'generate_idea') {
        const parentNodeId = result.edges?.[0]?.source
        if (parentNodeId && (result.phase !== undefined || result.branchType !== undefined)) {
          setNodes((prev) =>
            prev.map((node) =>
              node.id === parentNodeId
                ? {
                    ...node,
                    data: {
                      ...node.data,
                      ...(result.phase !== undefined && { phase: result.phase as IdeationPhase }),
                      ...(result.branchType !== undefined && { branchType: result.branchType }),
                      ...(result.branchReasoning !== undefined && {
                        branchReasoning: result.branchReasoning,
                      }),
                    },
                  }
                : node
            )
          )
        }
      }
    },
    onNodeAdded: () => {
      // Camera movement disabled - let user stay where they are when adding suggestions
    },
    onReady: () => {
      // do nothing. before it showed a chip but thats too spammy
    },
    onGenerateFeedbackForIdea: (nodeId) => void generateFeedbackForIdea(nodeId),
    onRegenerateFeedbackForChildren: (nodeId) => void regenerateFeedbackForChildren(nodeId),
    getEdges: () => reactFlowInstanceRef.current?.getEdges() ?? edges,
    getNodes: () => reactFlowInstanceRef.current?.getNodes() ?? nodes,
  })

  // Sync AI state to the pub/sub store for per-node subscriptions
  useEffect(() => {
    aiStateStoreRef.current.batchUpdate(ai.sourceNodes, ai.suggestions)
  }, [ai.sourceNodes, ai.suggestions])

  // AI generation handlers
  const handleGenerateIdeas = useCallback(
    async (nodeId: string) => {
      const currentNodes = getCurrentNodes()
      const node = currentNodes.find((n: Node<ReactFlowNodeData>) => n.id === nodeId)
      const backendNode = node ? toBackendNodeFormat(node) : undefined
      await ai.generateIdeas(nodeId, backendNode)
    },
    [ai, getCurrentNodes]
  )

  const handleGenerateNeedsFromVenture = useCallback(
    async (nodeId: string) => {
      const currentNodes = getCurrentNodes()
      const node = currentNodes.find((n: Node<ReactFlowNodeData>) => n.id === nodeId)
      const backendNode = node ? toBackendNodeFormat(node) : undefined

      await ai.generateNeedsFromVenture(nodeId, backendNode)
    },
    [ai, getCurrentNodes]
  )

  const handleSynthesizeNeedsFromResearch = useCallback(
    async (nodeId: string) => {
      const currentNodes = getCurrentNodes()
      const node = currentNodes.find((n: Node<ReactFlowNodeData>) => n.id === nodeId)
      const backendNode = node ? toBackendNodeFormat(node) : undefined
      await ai.synthesizeNeedsFromResearch(nodeId, backendNode)
    },
    [ai, getCurrentNodes]
  )

  return {
    ai,
    aiStateStoreRef,
    addAIGeneratedNodes,
    generateFeedbackForIdea,
    regenerateFeedbackForChildren,
    handleGenerateIdeas,
    handleGenerateNeedsFromVenture,
    handleSynthesizeNeedsFromResearch,
  }
}
